import React from 'react';
import clsx from 'clsx';
import type { ProgramLockStatus, ProgramSearchResult } from '../types/rpc-contracts';
import supabase from '../utils/supabaseClient';
import { summarizeCategoryBuckets, EMPTY_CATEGORY_SUMMARY, type CategoryBucketSummary } from '../utils/categoryBuckets';
import { coerceBoolean } from '../utils/coerceBoolean';
import styles from './ResultsTable.module.css';

type ExtendedProgramSearchResult = ProgramSearchResult & {
  categories?: Array<{ id: string; name: string }>;
  website?: string;
  url?: string;
  winners_url?: string;
  past_winners?: string;
};

interface ResultsTableProps {
  programs: ProgramSearchResult[];
  selectedProgramIds: Set<string>;
  setSelectedProgramIds: (s: Set<string>) => void;
  selectedSearchId: string | null;
  setSelectedSearchId: (id: string | null) => void;
  sort_by?: 'name' | 'categories';
  sort_direction?: 'asc' | 'desc';
  onSort: (key: 'name' | 'categories') => void;
  onOpenProgramCard: (program: ProgramSearchResult) => void | Promise<void>;
  searchTerm?: string;
  lockStatusMap?: Map<string, ProgramLockStatus>;
  onToggleLock?: (program: ProgramSearchResult, currentLocked: boolean) => Promise<void> | void;
  togglingLockIds?: Set<string>;
}

export const ResultsTable: React.FC<ResultsTableProps> = ({
  programs,
  selectedProgramIds,
  setSelectedProgramIds,
  selectedSearchId,
  setSelectedSearchId,
  sort_by,
  sort_direction,
  onSort,
  onOpenProgramCard,
  searchTerm,
  lockStatusMap,
  onToggleLock,
  togglingLockIds,
}) => {
  const categorySummaryCache = React.useRef<Map<string, CategoryBucketSummary>>(new Map());

  const highlightText = (text?: string | null): React.ReactNode => {
    const q = (searchTerm || '').trim();
    if (!text) return text ?? '';
    if (!q) return text;
    const terms = Array.from(new Set(q.split(/\s+/).filter((term) => term.length >= 2)));
    if (terms.length === 0) return text;
    const escaped = terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
    return (
      <span>
        {text.split(regex).map((part, index) =>
          regex.test(part)
            ? <mark key={index} className={styles.markHighlight}>{part}</mark>
            : <span key={index}>{part}</span>
        )}
      </span>
    );
  };

  const getVettingStatusLabel = (status: string | null | undefined): { label: string; tone: 'pending' | 'approved' | 'rejected' } => {
    const normalized = (status ?? 'yellow').toLowerCase();
    switch (normalized) {
      case 'green':
        return { label: 'Approved', tone: 'approved' };
      case 'red':
        return { label: 'Not suitable', tone: 'rejected' };
      default:
        return { label: 'Review', tone: 'pending' };
    }
  };

  const getQualityCheckLabel = (program: ProgramSearchResult): { label: string; done: boolean } => {
    const completed = program.qc_flag_vetting === true;
    return completed ? { label: 'completed', done: true } : { label: 'pending', done: false };
  };

  const handleQualityRedirect = (programId: string, baciCode?: string | null) => {
    // Use new quality-checkv2 route if baci_code is available
    if (baciCode) {
      window.location.hash = `#quality-checkv2?program_id=${encodeURIComponent(programId)}&program_code=${encodeURIComponent(baciCode)}`;
    } else {
      // Fallback to legacy route if baci_code is missing
      window.location.hash = `#quality-check?program_id=${encodeURIComponent(programId)}`;
    }
  };

  const isLocked = (program: ProgramSearchResult): boolean => {
    const status = lockStatusMap?.get(program.id);
    if (status && typeof status.data_locked === 'boolean') {
      return status.data_locked;
    }
    const embeddedLocked = (program as ProgramSearchResult & { data_locked?: unknown }).data_locked;
    return coerceBoolean(embeddedLocked);
  };

  const isToggling = (programId: string): boolean => togglingLockIds?.has(programId) ?? false;

  const lockTooltip = (programId: string): string | undefined => {
    const status = lockStatusMap?.get(programId);
    if (!status) return undefined;
    const by = status.locked_by ?? 'unknown';
    const when = status.locked_at ? new Date(status.locked_at).toLocaleString() : 'unknown';
    return `Locked by ${by} on ${when}`;
  };

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead className={styles.tableHead}>
          <tr className={styles.headerRow}>
            <th className={clsx(styles.headerCell, styles.checkboxCell, styles.sticky, styles.checkboxColumn)}>
              <input
                type="checkbox"
                id="results-table-select-all"
                name="selectAllPrograms"
                aria-label="Select all on page"
                checked={programs.length > 0 && programs.every((program) => selectedProgramIds.has(program.id))}
                onChange={(event) => {
                  if (event.target.checked) {
                    setSelectedProgramIds(new Set([...selectedProgramIds, ...programs.map((program) => program.id)]));
                  } else {
                    const newSet = new Set(selectedProgramIds);
                    programs.forEach((program) => newSet.delete(program.id));
                    setSelectedProgramIds(newSet);
                  }
                }}
              />
            </th>
            <th
              className={clsx(styles.headerCell, styles.baciCell, styles.sticky, styles.baciColumn)}
            >
              BACI
            </th>
            <th
              className={clsx(styles.headerCell, styles.sticky, styles.nameColumn, styles.sortable)}
              role="columnheader"
              aria-sort={sort_by === 'name' ? (sort_direction === 'asc' ? 'ascending' : 'descending') : 'none'}
              onClick={() => onSort('name')}
              aria-label="Sort by Program Name"
            >
              Program Name {sort_by === 'name' ? (sort_direction === 'asc' ? '▲' : '▼') : ''}
            </th>
            <th className={styles.headerCell}>
              Organization
            </th>
            <th className={styles.headerCell}>Program URL</th>
            <th className={styles.headerCell}>Past Winners</th>
            <th
              className={clsx(styles.headerCell, styles.sortable)}
              role="columnheader"
              aria-sort={sort_by === 'categories' ? (sort_direction === 'asc' ? 'ascending' : 'descending') : 'none'}
              onClick={() => onSort('categories')}
              aria-label="Sort by Categories"
            >
              Cats {sort_by === 'categories' ? (sort_direction === 'asc' ? '▲' : '▼') : ''}
            </th>
            <th className={styles.headerCell}>
              Wins
            </th>
            <th className={styles.headerCell}>
              Vetting / QC
            </th>
            <th className={clsx(styles.headerCell, styles.centerCell)}>
              Lock
            </th>
            <th className={clsx(styles.headerCell, styles.centerCell)}>
              QCheck
            </th>
            <th className={styles.headerCell}>Details</th>
          </tr>
        </thead>
        <tbody>
          {programs.map((program) => {
            const extended = program as ExtendedProgramSearchResult;
            const websiteUrl = program.website_url || extended.website || extended.url;
            const winnersUrl = program.past_winners_url || extended.winners_url || extended.past_winners;
            const categoryCount = program.category_count || extended.categories?.length || 0;
            const isSelected = selectedProgramIds.has(program.id);
            const isActiveRow = selectedSearchId === program.id;
            const vettingInfo = getVettingStatusLabel(program.vetting_status as string | null | undefined);
            const qcInfo = getQualityCheckLabel(program);

            return (
              <React.Fragment key={program.id}>
                <tr
                  className={clsx(
                    styles.tableRow,
                    isSelected && styles.selectedRow,
                    isActiveRow && styles.activeRow
                  )}
                >
                  <td
                    className={clsx(
                      styles.cell,
                      styles.centerCell,
                      styles.sticky,
                      styles.checkboxColumn,
                      isActiveRow && styles.activeSticky
                    )}
                  >
                    <input
                      type="checkbox"
                      name="selectedPrograms"
                      aria-label={`Select program ${program.name}`}
                      checked={isSelected}
                      onChange={(event) => {
                        const newSet = new Set(selectedProgramIds);
                        if (event.target.checked) newSet.add(program.id); else newSet.delete(program.id);
                        setSelectedProgramIds(newSet);
                      }}
                      onClick={(event) => event.stopPropagation()}
                    />
                  </td>
                  <td
                    className={clsx(
                      styles.cell,
                      styles.baciCell,
                      styles.sticky,
                      styles.baciColumn,
                      isActiveRow && styles.activeSticky
                    )}
                  >
                    {program.baci_program_code ? highlightText(program.baci_program_code.slice(0, 3).toUpperCase()) : '—'}
                  </td>
                  <td
                    className={clsx(
                      styles.cell,
                      styles.sticky,
                      styles.nameColumn,
                      styles.sortable,
                      isActiveRow && styles.activeSticky
                    )}
                    onClick={() => setSelectedSearchId(selectedSearchId === program.id ? null : program.id)}
                  >
                    <div className={styles.programInfo}>
                      <span className={styles.programNameText}>{highlightText(program.name)}</span>
                    </div>
                  </td>
                  <td className={styles.cell}>
                    {program.awarding_organization
                      ? <span title={program.awarding_organization} className={styles.programMeta}>{highlightText(program.awarding_organization)}</span>
                      : <span className={styles.placeholder}>—</span>}
                  </td>
                  <td className={styles.cell}>
                    {websiteUrl
                      ? (<a className={styles.programLink} href={websiteUrl} target="_blank" rel="noopener noreferrer" title={websiteUrl}>Program URL</a>)
                      : <span className={styles.placeholder}>—</span>}
                  </td>
                  <td className={styles.cell}>
                    {winnersUrl
                      ? (<a className={styles.programLink} href={winnersUrl} target="_blank" rel="noopener noreferrer" title={winnersUrl}>Past winners</a>)
                      : <span className={styles.placeholder}>—</span>}
                  </td>
                  <td className={styles.cell}>
                    {categoryCount > 0
                      ? categoryCount
                      : <span className={styles.categoriesPlaceholder}>0</span>}
                  </td>
                  <td className={styles.cell}>
                    {program.win_opportunities || 0}
                  </td>
                  <td className={styles.cell}>
                    <div className={styles.vettingColumn}>
                      <span
                        className={clsx(
                          styles.vettingStatusText,
                          vettingInfo.tone === 'approved' && styles.vettingApproved,
                          vettingInfo.tone === 'rejected' && styles.vettingRejected,
                          vettingInfo.tone === 'pending' && styles.vettingPending
                        )}
                      >
                        {vettingInfo.label}
                      </span>
                      <span className={styles.qcStatusText}>{qcInfo.label}</span>
                    </div>
                  </td>
                  <td className={clsx(styles.cell, styles.centerCell)}>
                    <button
                      type="button"
                      className={clsx(
                        styles.lockToggleButton,
                        isLocked(program) ? styles.lockActive : styles.lockInactive,
                        isToggling(program.id) && styles.lockLoading
                      )}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!onToggleLock || isToggling(program.id)) return;
                        void onToggleLock(program, isLocked(program));
                      }}
                      title={isLocked(program) ? (lockTooltip(program.id) ?? 'Unlock program') : 'Lock program'}
                      disabled={!onToggleLock || isToggling(program.id)}
                    >
                      {isToggling(program.id)
                        ? '…'
                        : isLocked(program)
                          ? '🔒'
                          : '🔓'}
                    </button>
                  </td>
                  <td className={clsx(styles.cell, styles.centerCell)}>
                    <button
                      onClick={(event) => { event.stopPropagation(); handleQualityRedirect(program.id, program.baci_program_code); }}
                      title="Open in Quality Check"
                      className={styles.qualityButton}
                    >
                      QCheck
                    </button>
                  </td>
                  <td className={styles.cell}>
                    <button
                      onClick={(event) => { event.stopPropagation(); onOpenProgramCard(program); }}
                      title="View"
                      className={clsx(styles.inlineActionButton, styles.viewButton)}
                    >
                      View
                    </button>
                  </td>
                </tr>
                {selectedSearchId === program.id && (
                  <tr>
                    <td colSpan={11} className={styles.inlineSummaryCell}>
                      <ProgramInlineSummary program={program} cacheRef={categorySummaryCache} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

interface ProgramInlineSummaryProps {
  program: ProgramSearchResult;
  cacheRef: React.MutableRefObject<Map<string, CategoryBucketSummary>>;
}

const ProgramInlineSummary: React.FC<ProgramInlineSummaryProps> = ({ program, cacheRef }) => {
  const categoryCount = Number(program.category_count ?? 0);
  const cachedSummary = cacheRef.current.get(program.id) ?? (categoryCount === 0 ? EMPTY_CATEGORY_SUMMARY : null);
  const [summary, setSummary] = React.useState<CategoryBucketSummary | null>(cachedSummary);
  const [loading, setLoading] = React.useState<boolean>(!cachedSummary && categoryCount > 0);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;

    if (categoryCount === 0) {
      if (!cacheRef.current.has(program.id)) {
        cacheRef.current.set(program.id, EMPTY_CATEGORY_SUMMARY);
      }
      setSummary(EMPTY_CATEGORY_SUMMARY);
      setLoading(false);
      setError(null);
      return () => {
        active = false;
      };
    }

    if (cacheRef.current.has(program.id)) {
      setSummary(cacheRef.current.get(program.id) || EMPTY_CATEGORY_SUMMARY);
      setLoading(false);
      setError(null);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const { data, error: fetchError } = await supabase
          .schema('prod')
          .rpc('get_program_category_buckets', { p_program_id: program.id });

        if (!active) return;

        if (fetchError) {
          throw fetchError;
        }

        const summaryData = summarizeCategoryBuckets(data ?? []);
        cacheRef.current.set(program.id, summaryData);
        setSummary(summaryData);
      } catch (fetchError) {
        if (!active) return;
        setError((fetchError as Error)?.message || 'Unable to load categories for this program');
        setSummary(null);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [cacheRef, program.id, categoryCount]);

  const counts = summary ?? EMPTY_CATEGORY_SUMMARY;
  const showNumbers = !!summary && !loading;
  const displayCount = (value: number) => {
    if (loading) return '…';
    if (!showNumbers) return '—';
    return String(value);
  };

  const totalOthers = (summary?.poetryShort ?? 0) + (summary?.coverAudio ?? 0);

  // Get prize structure from database fields (not calculated)
  const getPrizeStructure = () => {
    if (loading) return '…';

    const prizesPerCat = program.prizes_per_category;
    const hasTiered = program.has_tiered_prizes;

    if (!prizesPerCat) return 'Not specified';

    if (prizesPerCat === 1) {
      return '1 prize/cat';
    } else if (prizesPerCat === 3 && hasTiered) {
      return '3 prizes/cat (G/S/B)';
    } else if (hasTiered) {
      return `${prizesPerCat} prizes/cat (tiered)`;
    } else {
      return `${prizesPerCat} prize${prizesPerCat > 1 ? 's' : ''}/cat`;
    }
  };

  const statItems = [
    { label: 'Fiction', value: displayCount(counts.fiction) },
    { label: 'Non-Fiction', value: displayCount(counts.nonFiction) },
    { label: 'Children & YA', value: displayCount(counts.childrenYa) },
    { label: 'Others', value: displayCount(totalOthers) },
    {
      label: 'Prize Structure',
      value: getPrizeStructure()
    }
  ];

  const totalFromSummary = summary
    ? summary.fiction + summary.nonFiction + summary.childrenYa + summary.poetryShort + summary.coverAudio
    : categoryCount;

  return (
    <div className={styles.inlineSummary}>
      <div className={styles.statGrid}>
        {statItems.map((item) => (
          <div key={item.label} className={styles.statBox}>
            <div className={styles.statLabel}>{item.label}</div>
            <div className={styles.statValue}>{item.value}</div>
          </div>
        ))}
      </div>
      {error && <div className={styles.inlineError}>{error}</div>}
      <div className={styles.idRow}>
        <div className={styles.idInfo}>
          <span>Program ID:</span>
          <span className={styles.idValue}>{program.id}</span>
          <button
            type="button"
            className={styles.copyButton}
            onClick={(event) => {
              event.stopPropagation();
              const clipboard = navigator.clipboard;
              if (clipboard?.writeText) {
                void clipboard.writeText(program.id).catch(() => undefined);
              }
            }}
            title="Copy program ID"
          >
            Copy
          </button>
        </div>
        <div className={styles.metaNote}>
          Categories tracked: {loading ? '…' : totalFromSummary}
        </div>
      </div>
    </div>
  );
};

export default ResultsTable;
